import React from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import uuid from 'uuid';
import { omit } from 'lodash';

import { Modal, Button } from '../../../components';
import AdministratorForm from './form/AdministratorForm';
import { GET_ADMINISTRATORS_QUERY } from '../containers/AdministratorsList';

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.administrators.form.salonExistingsError',
        defaultMessage: 'Salon does not exists',
    },
    permissionsError: {
        id: 'pages.administrators.form.permissionsError',
        defaultMessage: 'Current user does not have permission to add administrators',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const ADD_ADMINISTRATOR_MUTATION = gql`
    mutation addAdministrator($input: AddAdministratorInput!) {
        addAdministrator(input: $input) {
            administrator {
                id
                firstName
                lastName
                phone
                email
                roles
            }
        }
    }
`;

const DEFAULT_VALUES = {
    firstName: '',
    lastName: '',
    phone: '+371',
    email: '',
    password: '',
    passwordRepeat: '',
};

function AddAdministratorDialog({ trigger, open, onClose, intl }) {
    const userId = useSelector(({ user }) => user.get('id'));
    const salon = useSelector(({ user }) => user.get('salon'));

    const methods = useForm({ defaultValues: DEFAULT_VALUES });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    const [addAdministrator] = useMutation(ADD_ADMINISTRATOR_MUTATION, {
        update: (store, { data: { addAdministrator: result } }) => {
            try {
                const data = store.readQuery({
                    query: GET_ADMINISTRATORS_QUERY,
                    variables: { salon },
                });
                if (data?.viewer?.salon?.administrators) {
                    store.writeQuery({
                        query: GET_ADMINISTRATORS_QUERY,
                        variables: { salon },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    administrators: [
                                        ...data.viewer.salon.administrators,
                                        result.administrator,
                                    ],
                                },
                            },
                        },
                    });
                }
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onModalClose = () => {
        reset(DEFAULT_VALUES);
        onClose();
    };

    const onSubmit = async formData => {
        const phone = (formData.phone || '').replace(/[^+\d]/g, '');
        try {
            await addAdministrator({
                variables: {
                    input: {
                        userId,
                        salonId: salon,
                        ...omit(formData, 'passwordRepeat'),
                        phone,
                    },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    addAdministrator: {
                        __typename: 'AddAdministratorPayload',
                        administrator: {
                            __typename: 'Administrator',
                            id: uuid.v4(),
                            firstName: formData.firstName || null,
                            lastName: formData.lastName || null,
                            email: formData.email || null,
                            phone: phone || null,
                            roles: [],
                        },
                    },
                },
            });
            onModalClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (
                    graphQLError.name === 'NotExists' &&
                    graphQLError.data?.error === 'SALON_DOES_NOT_EXIST'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.existingsError),
                    });
                } else if (
                    graphQLError.name === 'Forbidden' &&
                    graphQLError.data?.error === 'USER_DOES_NOT_HAVE_PERMISSION_TO_ADD'
                ) {
                    setError('root', {
                        message: intl.formatMessage(intlMessages.permissionsError),
                    });
                } else {
                    setError('root', {
                        message:
                            graphQLError.message ||
                            intl.formatMessage(intlMessages.defaultErrorMessage),
                    });
                }
                return;
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onModalClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.administrators.form.newAdministrator"
                        defaultMessage="New administrator"
                    />
                </Modal.Header>
                <Modal.Content>
                    <AdministratorForm mode="add" />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.administrators.form.create"
                            defaultMessage="Create"
                        />
                    </Button>
                    <Button onClick={onModalClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.administrators.form.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(AddAdministratorDialog);
